<template>
  <div class="page-shell evaluation-page">
    <div class="topbar evaluation-page__topbar">
      <div class="page-title evaluation-page__title">
        <h1><span class="title-icon">📊</span>资产评估申请</h1>
        <p>按个人主播与 MCN 机构维度审核资产评估申请，并支持已通过报告下载</p>
      </div>
    </div>

    <div class="card evaluation-list-card">
      <div class="search-bar evaluation-search-bar">
        <input v-model.trim="draftFilters.keyword" type="text" placeholder="评估编号 / 资产名称">
        <select v-model="draftFilters.userType">
          <option value="">全部用户类型</option>
          <option value="individual">个人</option>
          <option value="mcn">MCN机构</option>
        </select>
        <select v-model="draftFilters.status">
          <option value="">全部状态</option>
          <option value="draft">草稿</option>
          <option value="pending">待审核</option>
          <option value="approved">已通过</option>
          <option value="rejected">已驳回</option>
        </select>
        <button class="btn btn-outline evaluation-search-btn" @click="applySearch">
          <span>🔍</span>
          <span>查询</span>
        </button>
      </div>

      <div class="table-wrap evaluation-table-wrap">
        <table>
          <thead>
            <tr>
              <th>评估编号</th>
              <th>资产名称</th>
              <th>用户类型</th>
              <th>提交人</th>
              <th>提交时间</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="table-empty">加载中...</td>
            </tr>
            <tr v-else-if="!list.length">
              <td colspan="7" class="table-empty">暂无申请记录</td>
            </tr>
            <template v-else>
              <tr v-for="item in list" :key="item.id">
                <td class="evaluation-no">{{ item.evaluateNo }}</td>
                <td>
                  <div class="evaluation-asset-cell">
                    <div class="evaluation-asset-cell__name">{{ item.assetName }}</div>
                    <div v-if="item.reportUrl && item.status === 'approved'" class="evaluation-asset-cell__sub">报告已生成</div>
                  </div>
                </td>
                <td>
                  <span :class="['tag', item.userType === 'mcn' ? 'tag-purple' : 'tag-blue']">{{ item.userTypeText }}</span>
                </td>
                <td>{{ item.submitterName }}</td>
                <td>{{ item.submittedAt }}</td>
                <td><span :class="statusClass(item.status)">{{ item.statusText }}</span></td>
                <td>
                  <div class="evaluation-actions">
                    <button class="btn btn-sm btn-outline" @click="openDetail(item)">{{ item.status === 'pending' || item.status === 'draft' ? '去审核' : '查看详情' }}</button>
                    <button
                      v-if="item.status === 'approved' && item.reportUrl"
                      class="btn btn-sm btn-success"
                      @click="openReport(item.reportUrl)"
                    >
                      下载报告
                    </button>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="evaluation-pagination">
        <el-pagination
          background
          layout="total, prev, pager, next, jumper"
          :current-page="pagination.currentPage"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          @current-change="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { assetEvaluationList } from '../../../api/userMG'
import { downloadRemoteFile } from '../../../utils/download'

export default {
  name: 'evaluation-applications-index',
  data() {
    return {
      loading: false,
      list: [],
      filters: {
        keyword: '',
        status: '',
        userType: ''
      },
      draftFilters: {
        keyword: '',
        status: '',
        userType: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 15,
        total: 0,
        lastPage: 1
      }
    }
  },
  created() {
    const routeQuery = this.$route.query || {}
    const page = Number(routeQuery.page) > 0 ? Number(routeQuery.page) : 1

    this.filters = {
      keyword: routeQuery.keyword || '',
      status: routeQuery.status || '',
      userType: routeQuery.user_type || ''
    }
    this.draftFilters = Object.assign({}, this.filters)
    this.pagination.currentPage = page

    this.loadList(page)
  },
  methods: {
    async loadList(page) {
      this.loading = true

      const targetPage = page || this.pagination.currentPage
      const params = {
        page: targetPage,
        per_page: this.pagination.pageSize
      }

      if (this.filters.keyword) {
        params.keyword = this.filters.keyword
      }

      if (this.filters.status) {
        params.status = this.filters.status
      }

      if (this.filters.userType) {
        params.user_type = this.filters.userType
      }

      try {
        const res = await assetEvaluationList(params)

        if (!this.isListSuccess(res)) {
          this.list = []
          this.pagination.total = 0
          this.$message.error(this.getErrorMessage(res, '资产评估申请列表加载失败'))
          return
        }

        const meta = res.meta || {}

        this.list = Array.isArray(res.data) ? res.data.map(item => this.normalizeItem(item)) : []
        this.pagination.currentPage = Number(meta.current_page) || targetPage
        this.pagination.pageSize = Number(meta.per_page) || this.pagination.pageSize
        this.pagination.total = Number(meta.total) || 0
        this.pagination.lastPage = Number(meta.last_page) || 1
      } catch (error) {
        this.list = []
        this.pagination.total = 0
        this.$message.error(this.getErrorMessage(error, '资产评估申请列表加载失败'))
      } finally {
        this.loading = false
      }
    },
    normalizeItem(item) {
      const submitter = this.pickSubmitter(item)

      return {
        id: item.id,
        evaluateNo: item.evaluate_no || item.evaluation_no || item.num || '—',
        assetName: item.asset_name || item.assetName || '—',
        userType: item.user_type || item.userType || '',
        userTypeText: item.user_type_text || this.typeText(item.user_type || item.userType),
        submitterName: this.pickSubmitterName(item, submitter),
        submittedAt: item.submitted_at || item.created_at || item.submitTime || '—',
        status: item.status || '',
        statusText: item.status_text || this.statusText(item.status),
        reportUrl: item.report_url || ''
      }
    },
    pickSubmitter(item) {
      if (!item || typeof item !== 'object') return {}

      const candidates = [item.submitter, item.submitter_info, item.user, item.applicant]

      for (let i = 0; i < candidates.length; i += 1) {
        const target = candidates[i]

        if (target && Object.prototype.toString.call(target) === '[object Object]') {
          return target
        }
      }

      return {}
    },
    pickSubmitterName(item, submitter) {
      return submitter.name || submitter.nickname || submitter.real_name || item.submitter_name || item.applicant_name || item.user_name || item.creator_name || '—'
    },
    isListSuccess(res) {
      return !!(res && typeof res === 'object' && Array.isArray(res.data) && res.meta && !res.message && !res.detail && !res.errors)
    },
    statusText(status) {
      return {
        draft: '草稿',
        pending: '待审核',
        approved: '已通过',
        rejected: '已驳回'
      }[status] || status || '—'
    },
    typeText(type) {
      return {
        individual: '个人',
        mcn: 'MCN机构'
      }[type] || type || '—'
    },
    statusClass(status) {
      return {
        status: true,
        'status-reviewing': status === 'draft',
        'status-pending': status === 'pending',
        'status-approved': status === 'approved',
        'status-rejected': status === 'rejected'
      }
    },
    getErrorMessage(error, fallback) {
      if (!error) return fallback
      if (typeof error === 'string') return error
      if (error.message) return error.message
      if (error.detail) return error.detail
      if (error.errors) {
        const firstKey = Object.keys(error.errors)[0]
        const firstError = firstKey ? error.errors[firstKey] : ''

        if (Array.isArray(firstError)) {
          return firstError[0] || fallback
        }

        return firstError || fallback
      }

      return fallback
    },
    applySearch() {
      this.filters = Object.assign({}, this.draftFilters)
      this.pagination.currentPage = 1
      this.loadList(1)
    },
    handlePageChange(page) {
      this.loadList(page)
    },
    openDetail(item) {
      this.$router.push({
        path: '/applications/evaluation/' + item.id,
        query: {
          page: String(this.pagination.currentPage),
          keyword: this.filters.keyword,
          status: this.filters.status,
          user_type: this.filters.userType
        }
      })
    },
    async openReport(url) {
      if (!url) {
        this.$message.warning('当前记录暂无可下载报告')
        return
      }

      await downloadRemoteFile(url, {
        fileName: 'asset-evaluation-report.docx'
      })
    }
  }
}
</script>

<style scoped>
.evaluation-page {
  gap: 18px;
}

.evaluation-page__topbar {
  min-height: 70px;
  margin: -20px -20px 0;
  padding: 18px 24px 20px;
  background: #e9eef5;
}

.evaluation-page__title h1 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  line-height: 1.2;
}

.evaluation-page__title p {
  margin-top: 10px;
  font-size: 13px;
  color: #334155;
}

.title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 18px;
}

.evaluation-list-card {
  padding: 22px 22px 20px;
  border-radius: 16px;
}

.evaluation-search-bar {
  align-items: center;
  margin-bottom: 14px;
}

.evaluation-search-bar input {
  width: 240px;
  min-width: 240px;
}

.evaluation-search-bar select {
  width: 180px;
  min-width: 180px;
}

.evaluation-search-btn {
  min-width: 78px;
  padding: 8px 14px;
}

.evaluation-table-wrap {
  border-radius: 12px;
}

.evaluation-no {
  color: #0f172a;
  font-weight: 700;
}

.evaluation-asset-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.evaluation-asset-cell__name {
  font-weight: 600;
  color: #1e293b;
  word-break: break-word;
}

.evaluation-asset-cell__sub {
  font-size: 12px;
  color: #059669;
}

.evaluation-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.evaluation-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;
}

.table-empty {
  padding: 24px 12px;
  text-align: center;
  color: var(--cog-text-secondary);
}

@media (max-width: 768px) {
  .evaluation-page__topbar {
    margin: -16px -16px 0;
    padding: 16px;
  }

  .evaluation-list-card {
    padding: 16px;
  }

  .evaluation-search-bar input,
  .evaluation-search-bar select,
  .evaluation-search-btn {
    width: 100%;
    min-width: 0;
  }

  .evaluation-pagination {
    justify-content: center;
    overflow-x: auto;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/applications/evaluation/index.vue